import { useQuery } from '@tanstack/react-query';
import { parseHttpError } from '@upme/api-client';
import { VIP_PARTNER_PACK_SLOTS } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCreateVipPartnerPack, useRevokeVipPartnerGrant } from '../mutations';
import { vipPartnerPacksOptions } from '../vip-partner-queries';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('ru-RU', { timeZone: 'UTC' });
}

/**
 * Партнёрство: заведение пакета по факту офлайн-оплаты и отзыв ошибочной
 * выдачи. Заведение пакета VIP самому партнёру НЕ выдаёт — это соседняя
 * кнопка в блоке VIP, и сцеплять их значило бы зашивать в код то, что
 * решается двумя кликами.
 */
export function VipPartnerSection({ userId }: { userId: string }) {
  const packs = useQuery(vipPartnerPacksOptions(userId));
  const create = useCreateVipPartnerPack(userId);
  const revoke = useRevokeVipPartnerGrant(userId);
  const [slots, setSlots] = useState(String(VIP_PARTNER_PACK_SLOTS));
  const [note, setNote] = useState('');
  const [confirmGrantId, setConfirmGrantId] = useState<string | null>(null);

  async function submitPack() {
    const parsed = Number(slots);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 50) {
      toast.error('Слотов должно быть от 1 до 50');
      return;
    }
    try {
      await create.mutateAsync({ slots: parsed, ...(note.trim() ? { note: note.trim() } : {}) });
      setNote('');
      toast.success('Пакет заведён');
    } catch (err) {
      const apiErr = await parseHttpError(err);
      toast.error(apiErr?.message ?? 'Не удалось завести пакет');
    }
  }

  async function submitRevoke(grantId: string) {
    try {
      await revoke.mutateAsync(grantId);
      setConfirmGrantId(null);
      toast.success('Выдача отозвана');
    } catch (err) {
      const apiErr = await parseHttpError(err);
      toast.error(apiErr?.message ?? 'Не удалось отозвать выдачу');
    }
  }

  return (
    <div className="rounded-lg border bg-card p-6 space-y-4">
      <h2 className="font-medium">Gold</h2>

      <div className="flex items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="vip-partner-slots">Слотов</Label>
          <Input
            id="vip-partner-slots"
            type="number"
            className="w-24"
            min={1}
            max={50}
            value={slots}
            onChange={(e) => setSlots(e.target.value)}
          />
        </div>
        <div className="flex-1 space-y-1">
          <Label htmlFor="vip-partner-note">Заметка</Label>
          <Input
            id="vip-partner-note"
            maxLength={500}
            placeholder="чем и когда оплачено"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <Button type="button" disabled={create.isPending} onClick={() => void submitPack()}>
          Завести пакет
        </Button>
      </div>

      {packs.isPending ? (
        <p className="text-sm text-muted-foreground">Загрузка…</p>
      ) : packs.isError ? (
        <p className="text-sm text-destructive">Не удалось загрузить пакеты</p>
      ) : packs.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">Пакетов нет — человек не партнёр.</p>
      ) : (
        <div className="space-y-4 border-t pt-4">
          {packs.data.map((pack) => (
            <div key={pack.id} className="space-y-2">
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium">
                  Осталось {pack.slotsLeft} из {pack.slotsTotal}
                </span>
                <span className="text-muted-foreground">от {formatDate(pack.grantedAt)}</span>
                {pack.note && <span className="text-muted-foreground">· {pack.note}</span>}
                {pack.revokedAt && <span className="text-destructive">пакет отозван</span>}
              </div>
              {pack.grants.length === 0 ? (
                <p className="text-xs text-muted-foreground">Выдач не было</p>
              ) : (
                pack.grants.map((grant) => (
                  <div key={grant.id} className="flex items-center gap-3 text-sm">
                    <span>{grant.recipient.firstName ?? grant.recipient.username ?? '—'}</span>
                    <span className="text-muted-foreground">
                      {formatDate(grant.grantedAt)} · Pro до {formatDate(grant.vipUntil)}
                    </span>
                    {grant.revokedAt ? (
                      <span className="text-muted-foreground">отозвано</span>
                    ) : confirmGrantId === grant.id ? (
                      <>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          disabled={revoke.isPending}
                          onClick={() => void submitRevoke(grant.id)}
                        >
                          Подтвердить отзыв
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmGrantId(null)}
                        >
                          Отмена
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmGrantId(grant.id)}
                      >
                        Отозвать
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
